import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoIcon from "@mui/icons-material/Info";
import EventIcon from "@mui/icons-material/Event";
import VideocamIcon from "@mui/icons-material/Videocam";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

const MobileEventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!id) {
        setEvent(null);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/events`);
        const foundEvent = response.data.find((e) => e.id === id);
        if (foundEvent) {
          setEvent(foundEvent);
        } else {
          setError("未找到活动详情");
        }
      } catch (err) {
        console.error("获取活动详情失败:", err);
        setError("获取活动详情失败，请稍后再试");
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [id]);

  // 计算倒计时
  useEffect(() => {
    if (!event || !event.time) return;

    const calculateCountdown = () => {
      const now = new Date();
      const eventTime = new Date(event.time);
      const diff = eventTime - now;

      if (diff <= 0) {
        setCountdown(null);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setCountdown({ days, hours });
      } else if (hours > 0) {
        setCountdown({ hours, minutes });
      } else {
        setCountdown({ minutes });
      }
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 60000); // 每分钟更新一次
    return () => clearInterval(interval);
  }, [event]);

  const formatCountdown = (countdown) => {
    if (!countdown) return null;
    if (countdown.days) return `${countdown.days}天${countdown.hours}时`;
    if (countdown.hours) return `${countdown.hours}时${countdown.minutes}分`;
    return `${countdown.minutes}分`;
  };

  const addToCalendar = () => {
    if (!event) return;
    const eventTitle = encodeURIComponent(event.title);
    const eventDesc = encodeURIComponent(event.description);
    const eventStart = new Date(event.time).toISOString().replace(/-|:|\.\d+/g, "");
    const eventEnd = new Date(new Date(event.time).getTime() + 2 * 60 * 60 * 1000)
      .toISOString().replace(/-|:|\.\d+/g, "");
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${eventTitle}&details=${eventDesc}&dates=${eventStart}/${eventEnd}`;
    window.open(googleCalendarUrl, "_blank");
  };

  const textColor = prefersDarkMode ? "#ffffff" : "#000000";
  const secondaryTextColor = prefersDarkMode ? "rgba(235, 235, 245, 0.6)" : "rgba(60, 60, 67, 0.6)";
  const borderColor = prefersDarkMode ? "rgba(84, 84, 88, 0.65)" : "rgba(0, 0, 0, 0.1)";

  if (loading) {
    return (
      <Box sx={{ px: 2, py: 2.5, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress size={24} sx={{ color: textColor }} />
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Box sx={{ px: 2, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: textColor, mr: 1 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 600, color: textColor }}>
            返回
          </Typography>
        </Box>
        <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem", textAlign: "center", py: 4 }}>
          {error || "未找到活动详情"}
        </Typography>
      </Box>
    );
  }

  const formattedCountdown = formatCountdown(countdown);

  return (
    <Box sx={{ px: 2, py: 2.5, pb: 4 }}>
      {/* 返回按钮和标题 */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: textColor, mr: 1, padding: 0.5 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 600, color: textColor }}>
          {event.title}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: borderColor, mb: 2 }} />

      {/* 倒计时 */}
      {formattedCountdown && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: "12px",
            background: prefersDarkMode
              ? "rgba(10, 132, 255, 0.15)"
              : "rgba(10, 132, 255, 0.08)",
            border: "0.5px solid rgba(10, 132, 255, 0.2)",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: secondaryTextColor,
              mb: 0.5,
            }}
          >
            还剩
          </Typography>
          <Typography
            sx={{
              fontSize: "1.1rem",
              fontWeight: 700,
              color: "#0a84ff",
            }}
          >
            {formattedCountdown}
          </Typography>
        </Box>
      )}

      {/* 活动时间 */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <AccessTimeIcon sx={{ fontSize: "0.85rem", color: secondaryTextColor, mt: 0.25 }} />
        <Box>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: secondaryTextColor,
              mb: 0.5,
            }}
          >
            活动时间
          </Typography>
          <Typography
            sx={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: textColor,
            }}
          >
            {event.displayTime}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: borderColor, mb: 2 }} />

      {/* 活动详情 */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
          <InfoIcon sx={{ fontSize: "0.85rem", color: secondaryTextColor }} />
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: secondaryTextColor,
            }}
          >
            活动详情
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "0.85rem",
            color: textColor,
            lineHeight: 1.6,
            whiteSpace: "pre-line",
            pl: 2.1,
          }}
        >
          {event.description}
        </Typography>
      </Box>

      {/* Zoom会议信息 */}
      {event.zoomlink && event.zoompassword && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: "12px",
            border: `0.5px solid ${borderColor}`,
            background: prefersDarkMode
              ? "rgba(66, 133, 244, 0.1)"
              : "rgba(66, 133, 244, 0.05)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
            <VideocamIcon sx={{ fontSize: "0.85rem", color: "#4285f4" }} />
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#4285f4",
              }}
            >
              线上会议
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontFamily: "monospace",
              color: textColor,
              mb: 0.5,
              pl: 2.1,
            }}
          >
            ID: {event.zoomlink}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontFamily: "monospace",
              color: textColor,
              pl: 2.1,
            }}
          >
            密码: {event.zoompassword}
          </Typography>
        </Box>
      )}

      {/* 操作按钮 */}
      <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          fullWidth
          sx={{
            borderRadius: "10px",
            borderColor: borderColor,
            color: textColor,
            textTransform: "none",
            fontSize: "0.8rem",
            py: 0.875,
          }}
        >
          返回
        </Button>
        <Button
          variant="contained"
          onClick={addToCalendar}
          startIcon={<EventIcon sx={{ fontSize: "0.85rem" }} />}
          fullWidth
          sx={{
            borderRadius: "10px",
            backgroundColor: "#0a84ff",
            color: "#ffffff",
            textTransform: "none",
            fontSize: "0.8rem",
            py: 0.875,
            "&:hover": {
              backgroundColor: "#0051d5",
            },
          }}
        >
          添加到日历
        </Button>
      </Box>
    </Box>
  );
};

export default MobileEventDetail;

